import React, { useState } from 'react';
import { SketchPicker } from 'react-color';
import { Box } from "@mui/system";
import { ClickAwayListener, Popover } from '@mui/material';
import { CustomTextField } from './CustomTextField';
import CustomChip from './CustomChip';
import {CustomStyleColorPicker, CustomStyleDivColorPicker} from './Styles';
import {useTranslation} from 'react-i18next';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 * @Description Component to select a hexadecimal color and preview the color. It is important to emit only one event for color updates (setColor, onChange).
 * @param String id: Identifier for the ColorPicker (required)
 * @param String title: Text for the label (optional)
 * @param String name: Name for the input del color (The name attribute is required when handling changes with onChange)
 * @param String color: Hexadecimal value for the default color (optional)
 * @param useState setColor: state to update the color value (setColor works even if onChange is not passed as a prop)
 * @param boolean preview: Show preview of the color under the color picker (required)
 * @param event onChange: event to update the color value (onChange works even if setColor is not passed as a prop)
 * @param String chipLegend: Text for the legend (default value: large)
 * @param String chipVariant: If variant=compact, all edges are rounded, else
 * @param boolean titleInHeader: If true, display the title in the component's header; otherwise, show the title inside the CustomTextField input.
 * @returns {JSX.Element}
 */
const ColorPicker = ({ id, title = '', name, color, setColor, preview = false,
                         onChange = null, chipLegend = 'preview', chipVariant = 'large', titleInHeader=false }) => {

    const { t } = useTranslation();
    const isValidColor = isValidHexColor(color);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorChange = (newColor) => {
        if (onChange && !setColor)
            onChange({ target: { name: name, value: newColor.hex } });

        if (!onChange && setColor)
            setColor(newColor.hex)
    };

    function isValidHexColor(hex) {
        return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
    }

    return (
        <>
            {titleInHeader && <div className='mb-3'><p className={'ibl-h5-general-950 ibl-font-w-600'}>{t(title)}</p></div> }
            <ClickAwayListener onClickAway={handleClose}>
                <div className='d-flex align-items-center gap-3'>
                    <div onClick={handleClick} style={CustomStyleDivColorPicker}>
                        <Box sx={CustomStyleColorPicker(color)} />
                    </div>

                    <CustomTextField
                        id={id}
                        value={color}
                        trans={!titleInHeader ? title : ''}
                        type='text'
                        readOnly
                        click={handleClick}
                        showCounter={false}
                        marginBottom={false}
                    />

                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        disableAutoFocus
                        disableEnforceFocus
                    >
                        <Box sx={{ p: 2 }}>
                            <SketchPicker
                                id={id}
                                color={color}
                                onChange={(e) => {
                                    handleColorChange(e)
                                }}
                                disableAlpha
                            />
                        </Box>
                    </Popover>
                </div>
            </ClickAwayListener>
            { (preview && isValidColor) &&
                <div className="px-0 py-2">
                    <CustomChip
                        background={color}
                        legend={t(chipLegend)}
                        className='w-100'
                        variant={chipVariant}
                    />
                </div>
            }
        </>
    );
};

export default ColorPicker;
